/**
 * @module components/ACS/ACSMinutesPanel
 * @description Panel de visualización de minuta post-sesión ACS.
 *
 * Muestra el resultado de la generación de minutas por Gemini al finalizar una sesión.
 * Tiene dos modos de visualización:
 * - **Estructurada** (`status='final'`): muestra resumen, decisiones clave, ítems de acción,
 *   riesgos identificados y tareas propuestas con checklist interactivo para el CISO
 * - **Raw** (`status='needs_review'`): muestra el texto sin procesar de Gemini con
 *   advertencia de que requiere revisión manual (parsing falló)
 *
 * Las tareas propuestas pueden editarse antes de enviarlas al CISO para aprobación.
 *
 * @see minutesSchema.ts para la validación Zod que determina el status
 * @see ACSSessionView que renderiza este panel al finalizar la sesión
 * @see ACSProposalReview para la aprobación final del CISO
 */
// ============================================================
// ACSMinutesPanel — Visualización de minuta post-sesión
// Muestra el resultado estructurado (o raw si parsing falló)
// ============================================================

import React from 'react';
import { X, AlertCircle, CheckCircle2, FileText, Target, Shield, Send } from 'lucide-react';
import type { ACSMinutes, ACSProposedTaskData } from '../../types/acs';

interface ACSMinutesPanelProps {
  minutes:           ACSMinutes;
  localProposals:    ACSProposedTaskData[];
  onSubmitProposals: (proposals: ACSProposedTaskData[]) => void;
  onClose:           () => void;
}

export function ACSMinutesPanel({
  minutes,
  localProposals,
  onSubmitProposals,
  onClose,
}: ACSMinutesPanelProps) {
  const isNeedsReview = minutes.status === 'needs_review';
  const hasParsedData = minutes.status === 'final' || minutes.status === 'draft';

  const allProposals = [
    ...(minutes.proposedTasks ?? []),
    ...localProposals,
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
              isNeedsReview
                ? 'bg-yellow-100 dark:bg-yellow-900/30'
                : 'bg-green-100 dark:bg-green-900/30'
            }`}>
              {isNeedsReview
                ? <AlertCircle className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                : <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
              }
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Minuta de Sesión ACS</h2>
              <p className={`text-xs ${isNeedsReview ? 'text-yellow-600 dark:text-yellow-400' : 'text-green-600 dark:text-green-400'}`}>
                {isNeedsReview ? 'Requiere revisión manual' : 'Generada correctamente'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Advertencia si needs_review */}
          {isNeedsReview && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300 mb-1">
                La minuta requiere revisión manual
              </p>
              <p className="text-xs text-yellow-700 dark:text-yellow-400">
                No se pudo estructurar automáticamente. Revisa el texto completo abajo.
                Las propuestas de tareas no fueron generadas automáticamente.
              </p>
              {minutes.parseError && (
                <p className="text-[11px] text-yellow-600 dark:text-yellow-500 mt-2 font-mono">
                  Error: {minutes.parseError}
                </p>
              )}
            </div>
          )}

          {/* Resumen */}
          {hasParsedData && minutes.summary && (
            <section>
              <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                Resumen Ejecutivo
              </h3>
              <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                {minutes.summary}
              </p>
            </section>
          )}

          {/* Decisiones clave */}
          {hasParsedData && minutes.keyDecisions && minutes.keyDecisions.length > 0 && (
            <section>
              <h3 className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                <Target className="w-3.5 h-3.5" />
                Decisiones Clave
              </h3>
              <ul className="space-y-1">
                {minutes.keyDecisions.map((d, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <CheckCircle2 className="w-3.5 h-3.5 text-blue-500 flex-shrink-0 mt-0.5" />
                    {d}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Riesgos identificados */}
          {hasParsedData && minutes.risksIdentified && minutes.risksIdentified.length > 0 && (
            <section>
              <h3 className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                <Shield className="w-3.5 h-3.5" />
                Riesgos Identificados
              </h3>
              <ul className="space-y-1">
                {minutes.risksIdentified.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <AlertCircle className="w-3.5 h-3.5 text-orange-500 flex-shrink-0 mt-0.5" />
                    {r}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Propuestas de tareas */}
          {allProposals.length > 0 && (
            <section>
              <h3 className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                <FileText className="w-3.5 h-3.5" />
                Propuestas de Tareas ({allProposals.length})
              </h3>
              <div className="space-y-2">
                {allProposals.map((t, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold flex-shrink-0 mt-0.5 ${
                      t.priority === 'critical' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                      t.priority === 'high'     ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
                      t.priority === 'medium'   ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                                                  'bg-gray-100 text-gray-600 dark:bg-gray-600 dark:text-gray-300'
                    }`}>
                      {t.priority}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{t.title}</p>
                      {t.description && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{t.description}</p>
                      )}
                      {t.estimatedDueDate && (
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Fecha estimada: {t.estimatedDueDate}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                Estas propuestas quedarán en estado "Pendiente de revisión" hasta que el CISO las apruebe o rechace.
              </p>
            </section>
          )}

          {/* Texto raw (siempre disponible) */}
          <details className="mt-4">
            <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600 dark:hover:text-gray-300">
              Ver transcripción completa
            </summary>
            <pre className="mt-2 text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-900 rounded-lg p-4 overflow-auto max-h-64 whitespace-pre-wrap">
              {minutes.rawText}
            </pre>
          </details>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Generada: {new Date(minutes.generatedAt).toLocaleString('es-CL')}
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              Cerrar
            </button>
            {allProposals.length > 0 && (
              <button
                onClick={() => onSubmitProposals(allProposals)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-medium"
              >
                <Send className="w-4 h-4" />
                Enviar {allProposals.length} propuesta(s) al CISO
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
